import { ChevronRight } from 'lucide-react';
import { Avatar, Card, Typography } from '../ui';
import { contactDisplayName, shortRelativeTime, longRelativeTime, singleOutreachId } from '../../../lib/contact-rows';
import { contactStage, mostPressingThread, stageBiValue } from '../../../lib/outreach-stage';
import { StageDot } from './StageDot';
import { StageLine } from './StageLine';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useNarrow } from '../../../lib/use-narrow';
import type { ContactId, WebContact } from '../../../lib/contact-book';
import { useTranslation } from '../../../lib/i18n';
import styles from './contacts.legacy.module.css';

/**
 * One person in the Outreach list. Shared 1:1 with iOS `ContactRowView` and Android `ContactRow`.
 *
 * Two per-viewport anatomies, both straight from Figma (web `17937:268706`, native `17937:264687`):
 *  - **desktop** — avatar · [dot + name / the most pressing thread's stage line, else "Last update:
 *    {when}"]. No chevron. Card `lg` (20 radius / 20 padding).
 *  - **compact** (mobile-web + the native phones) — avatar · [dot + name / the stage line] · short time
 *    (`2d`) + a chevron. Card `sm`+sunken (16 radius / 12·16).
 *
 * The dot is the CONTACT's stage (`contactStage` — the server's, else the worst of their threads); the line
 * is the most pressing thread's (`mostPressingThread`). A contact nobody has messaged has neither.
 */
export function ContactRow({
  entry,
  onOpen,
}: {
  entry: WebContact;
  /** Opens the contact by its identity — a person, or (address-only, #10660) a recipient. `outreachId`
   *  is the contact's ONE conversation, to open straight in the viewer; otherwise undefined and the
   *  caller opens the contact screen to choose from several. */
  onOpen: (id: ContactId, outreachId?: string) => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const narrow = useNarrow();
  const card = entry.contact;
  const personId = card?.personId ?? '';
  // A counterparty the people book knows only by an address has no person id — it opens by recipient
  // id instead (#10660), rather than being an inert row.
  const recipientId = entry.recipientId ?? '';
  const contactId: ContactId | null = personId ? { personId } : recipientId ? { recipientId } : null;
  // The contact's stage (null for a contact nobody has messaged: no dot, because never-contacted and
  // finished are different facts), and the one thread whose line the row quotes.
  const stage = contactStage(entry);
  const pressing = mostPressingThread(entry);
  const displayName = contactDisplayName(card, entry.channels, t('contacts.row.unnamed'));

  const open = () => {
    if (!contactId) return;
    // person vs address-only recipient (#10660), so recipientId-only opens are distinguishable.
    logPageClickBi(
      { page_name: 'contacts_page', button_name: 'open_contact' },
      { additionalFields: { contact_kind: personId ? 'person' : 'recipient', ...(stage ? { stage: stageBiValue(stage) } : {}) } },
    );
    // A contact with exactly ONE conversation opens it in the viewer — the contact screen would
    // otherwise be a list of one. More than one has no single answer, so it opens that screen.
    onOpen(contactId, singleOutreachId(entry) ?? undefined);
  };

  const nameEl = (
    <span className={styles.stageNameRow}>
      <StageDot stage={stage} />
      <Typography variant="h4" color="primary" className={styles.rowName}>
        {displayName}
      </Typography>
    </span>
  );
  const stageLine = pressing ? <StageLine thread={pressing.thread} contactName={displayName} className={styles.rowSubtitle} /> : null;

  return (
    <Card
      variant="sunken"
      size={narrow ? 'sm' : 'lg'}
      component="button"
      interactive
      className={narrow ? styles.rowCompact : styles.row}
      onClick={open}
      aria-disabled={!contactId}
    >
      <Avatar
        initials={avatarInitials(displayName)}
        photoUrl={card?.effectiveAvatarUrl}
        size={40}
        color={avatarSlotFor(personId || recipientId || card?.displayName || '')}
      />

      {narrow ? (
        // Compact: the end cluster's vertical alignment follows the content — top with a stage line,
        // centre with the name alone.
        <span className={stageLine ? styles.rowRightTop : styles.rowRightCenter}>
          {stageLine ? (
            <span className={styles.rowMainCompact}>
              {nameEl}
              {stageLine}
            </span>
          ) : (
            nameEl
          )}
          <span className={styles.rowEnd}>
            <Typography variant="p3" color="secondary" className={styles.rowAge}>
              {shortRelativeTime(entry.lastActivityAt, undefined, t('contacts.time.now'))}
            </Typography>
            <ChevronRight size={24} className="dir-flip" aria-hidden />
          </span>
        </span>
      ) : (
        // Desktop: name over the stage line, else "Last update: …". A person in the book nobody has written
        // to has neither, so the line is left out rather than reading "Last update:" over nothing — the
        // same no-time rendering both phones give that row.
        <>
          <span className={styles.rowMain}>
            {nameEl}
            {stageLine ?? (entry.lastActivityAt ? (
              <Typography variant="p3" color="secondary" className={styles.rowSubtitle}>
                {t('contacts.row.lastUpdate', { time: longRelativeTime(entry.lastActivityAt) })}
              </Typography>
            ) : null)}
          </span>
        </>
      )}
    </Card>
  );
}
